import service from './services';

/**
 * Start an async report generation and poll until complete.
 *
 * @param {string} type - Report type: signin_sheet, employee_confirmed, active_report, payroll, wishlist_summary
 * @param {object} headers - Auth and context headers (Authorization, cs3-context-role, cs3-context-clientId)
 * @param {object} params - Report-specific params (job_id, employee_id, report_id, type, month, year, etc.)
 * @returns {Promise<{gcs_path: string, result_meta: object}>}
 */
export function generateReportAsync(type, headers, params = {}) {
    // task_type carries the pipeline-level report type (signin_sheet, active_report, ...);
    // callers may set their own `type` inside params (e.g. daily_schedule, custom, employee)
    // which is consumed by ReportGeneratorService::generateActiveReport and must pass through.
    return service.post('reports/task', { ...params, task_type: type }, headers)
        .then(function (startRes) {
            var taskId = startRes.data.result.task_id;
            return pollForCompletion(taskId, headers);
        });
}

function pollForCompletion(taskId, headers) {
    var POLL_INTERVAL = 2500;
    var MAX_WAIT = 300000; // 5 minutes

    return new Promise(function (resolve, reject) {
        var stopped = false;
        var startTime = Date.now();

        function poll() {
            if (stopped) return;

            if (Date.now() - startTime > MAX_WAIT) {
                stopped = true;
                reject(new Error('Report generation timed out. Please try again.'));
                return;
            }

            service.get('reports/task/' + taskId, headers)
                .then(function (pollRes) {
                    if (stopped) return;
                    var task = pollRes.data.result;

                    if (task.status === 'completed') {
                        stopped = true;
                        resolve(task);
                    } else if (task.status === 'failed') {
                        stopped = true;
                        reject(new Error(task.error_message || 'Report generation failed'));
                    } else {
                        setTimeout(poll, POLL_INTERVAL);
                    }
                })
                .catch(function () {
                    if (!stopped) {
                        setTimeout(poll, POLL_INTERVAL * 2);
                    }
                });
        }

        poll();
    });
}
